import { useState, useEffect } from 'react'
import { ArrowLeft, Bookmark } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Post from '../components/Post'
import api from '../services/api'

export default function Bookmarks() {
  const navigate = useNavigate()
  const [bookmarkedPosts, setBookmarkedPosts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchBookmarks()
  }, [])

  const fetchBookmarks = async () => {
    try {
      setLoading(true)
      setError(null)

      const result = await api.getBookmarkedPosts({ limit: 50 })

      if (result.success && result.posts.length > 0) {
        const normalizedPosts = result.posts.map((post: any) => ({
          id: post.id || post.postId,
          authorId: post.userId || post.authorId,
          author: post.author || {
            name: post.authorName || 'Unknown',
            username: post.authorUsername || 'unknown',
            avatar: post.authorAvatar,
            verified: post.authorVerified,
          },
          content: post.content || post.text || '',
          mediaUrl: post.mediaUrl || post.mediaUri,
          mediaType: post.mediaType,
          createdAt: post.createdAt,
          capturedAt: post.capturedAt || null,
          location: post.location ? {
            address: typeof post.location === 'string' ? post.location :
              (post.location.city
                ? post.location.state
                  ? `${post.location.city}, ${post.location.state}`
                  : post.location.city
                : post.location.address || undefined)
          } : undefined,
          onRecordCode: post.onRecordCode || post.postCode,
          stats: post.stats || {
            likes: post.likesCount || 0,
            comments: post.commentsCount || 0,
            reposts: post.repostsCount || 0,
            views: post.viewsCount || 0,
          }
        }))
        setBookmarkedPosts(normalizedPosts)
      } else {
        setBookmarkedPosts([])
      }
    } catch (err: any) {
      console.error('Error fetching bookmarks:', err)
      setError(err.message || 'Failed to load bookmarks')
      setBookmarkedPosts([])
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 bg-white/90 backdrop-blur-sm border-b border-gray-200 z-10">
        <div className="flex items-center gap-4 px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="rounded-full p-1.5 hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold">Bookmarks</h1>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-onrecord-orange"></div>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
            <Bookmark className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-lg font-bold mb-2">Failed to load bookmarks</h2>
          <p className="text-gray-500 text-center text-sm mb-4">{error}</p>
          <button
            onClick={fetchBookmarks}
            className="px-4 py-2 bg-onrecord-orange text-white rounded-full text-sm font-semibold hover:bg-onrecord-orange-dark transition-colors"
          >
            Try again
          </button>
        </div>
      ) : bookmarkedPosts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Bookmark className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Save posts for later</h2>
          <p className="text-gray-500 text-center max-w-md mb-6">
            Bookmark posts to easily find them again in the future. Your bookmarks are private.
          </p>
          <div className="text-sm text-gray-400 bg-gray-50 rounded-lg px-4 py-3 max-w-md">
            <p className="font-semibold mb-1">How to bookmark:</p>
            <p>Tap the bookmark icon on any post to save it here.</p>
          </div>
        </div>
      ) : (
        <div>
          {bookmarkedPosts.map((post) => (
            <Post key={post.id} post={post} />
          ))}
        </div>
      )}
    </div>
  )
}
